import React from 'react';
import { formatBatteryPercentage, isBatteryZero } from '../../../utils/normalizeEvents';

function getBatteryChargeStatus(percentage) {
  if (percentage < 35) {
    return { color: '#EC7104', opacity: 1 };
  }
  return { color: '#22AE93', opacity: 1 };
}

const BatteryArcIndicator = ({
  percentage = 0, // Battery percentage (0 - 100)
  segments = 20, // Total number of segments in the arc
  radius = 150, // Radius of the semicircle
  strokeWidth = 12, // Stroke width of each line
  inactiveColor = '#e0e0e0', // Color for inactive segments
  lineLength = 20, // Length of each segment line
}) => {
  // Determine the overall status based on the battery percentage.

  const { color: activeColor, opacity: activeOpacity } = getBatteryChargeStatus(percentage);

  // Calculate the number of segments that should be active.
  const activeCount = Math.floor((percentage / 100) * segments);

  const numberXPercentagePosition = percentage === 100 ? '76%' : '70%';

  // Generate the SVG lines for the semicircular arc.
  const segmentsElements = Array.from({ length: segments }, (_, i) => {
    // Calculate the angle for each segment along a semicircle
    const angle = Math.PI * (1 - i / (segments - 1));

    // Calculate the starting point of the line at the outer edge of the arc.
    const x1 = radius + (radius - strokeWidth) * Math.cos(angle);
    const y1 = radius - (radius - strokeWidth) * Math.sin(angle);

    // Calculate the end point of the line (inset by lineLength).
    const x2 = radius + (radius - strokeWidth - lineLength) * Math.cos(angle);
    const y2 = radius - (radius - strokeWidth - lineLength) * Math.sin(angle);

    // Determine the segment color and opacity: active segments use the active values.
    const isActive = i < activeCount;

    const getSegmentColor = () => {
      if (isActive) return activeColor;
      return inactiveColor;
    };

    const segmentColor = getSegmentColor();
    const segmentOpacity = isActive ? activeOpacity : 1; // You could also add an inactive opacity if desired.

    return (
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={segmentColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={segmentOpacity}
      />
    );
  });

  const showPercentageCharacter = !isBatteryZero(percentage);
  const formattedBatteryValue = formatBatteryPercentage(percentage);
  const numberXPosition = showPercentageCharacter ? '45%' : '50%';

  return (
    <svg width={radius * 2} height={radius + lineLength}>
      {segmentsElements}
      <text
        x={numberXPosition}
        y="80%"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize={showPercentageCharacter ? radius / 1.7 : radius / 2}
        fill="#697077"
      >
        {formattedBatteryValue}
      </text>
      {showPercentageCharacter && (
        <text
          x={numberXPercentagePosition}
          y="85%"
          dominantBaseline="middle"
          textAnchor="middle"
          fontSize={radius / 3.5}
          fill="#697077"
        >
          %
        </text>
      )}
    </svg>
  );
};

export default React.memo(BatteryArcIndicator);
